import moment from "moment";
import { SvgIcon } from "@progress/kendo-react-common";
import { circleIcon } from "@progress/kendo-svg-icons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import EndpointV3Image from "../../../../Assets/GBOSS_Endpoint-V3.png";
import GatewayImage from "../../../../Assets/GBOSS_Gateway.png";
import HeadUnitImage from "../../../../Assets/GBOSS_Head-Unit.png";
import EndpointMP2Image from "../../../../Assets/EMP2.png";
import EndpoinBulkFlow from "../../../../Assets/Endpoint-BF.png";
import { DeviceType } from "../../../../Enums/DeviceType";
import GatewayHMEImage from "../../../../Assets/hmeGateway.png";
import { enMessages } from "../../../../messages/en-US";
import { useLocalization } from "@progress/kendo-react-intl";
import { formatSmartDate } from "../../../Shared/Utilities/DateFormattingUtils";

interface DeviceDetailsHeaderProps {
  deviceData: any;
  deviceType: string;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Healthy':
    case 'Online':
      return 'text-gbBrand-green-700';
    case 'Unhealthy':
    case 'Offline':
      return 'text-gbBrand-red-500';
    case 'Warning':
      return 'text-gbBrand-yellow-500';
    default:
      return 'text-gbBrand-blue-500';
  }
};

const DeviceImageTile = ({ deviceData, deviceType }: DeviceDetailsHeaderProps) => {

  const getDeviceImage = () => {
    switch (deviceData?.deviceType) {
      case 'HeadUnit': return HeadUnitImage;
      case DeviceType.EndpointLF: return EndpointV3Image;
      case DeviceType.EndpointHF: return EndpointV3Image;
      case "Endpoint": return EndpointV3Image;
      case DeviceType.EndpointBF: return EndpoinBulkFlow;
      case DeviceType.EndpointMP2: return EndpointMP2Image;
      case 'Gateway': return GatewayImage;
      case 'GatewayHME': return GatewayHMEImage;
      default: return EndpointV3Image;
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
      <div className="flex flex-col items-center justify-center gap-42 h-full">
        <img 
          src={getDeviceImage()} 
          alt="Device" 
          className="object-contain h-full" 
          style={{maxHeight: "100px"}} 
        />
        <div className="flex flex-row items-center gap-1">
          <span className="text-sm font-semibold text-gray-800">
            {deviceData?.serialNumber}
          </span>
        </div>
        <div className="flex flex-row items-center gap-1">
          <div className="flex flex-row items-center gap-1">
            <SvgIcon icon={circleIcon} size="large" className={getStatusColor(deviceData?.connectivityStatus)} />
            <span className="text-sm font-semibold text-gray-800">{deviceData?.connectivityStatus}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const DeviceInfoTile = ({ deviceData, deviceType }: DeviceDetailsHeaderProps) => {
  const localizationService = useLocalization();
  const InfoField = ({ label, value }: { label: string; value?: string }) => (
    <div className="flex flex-col items-start gap-1">
      <span className="text-xs text-gray-500 font-medium">{label}:</span>
      <span className="text-sm font-semibold text-gray-800">{value || '-'}</span>
    </div>
  );

  const fields = [
    { label: localizationService.toLanguageString('custom.modelNumber', enMessages.custom.modelNumber), value: deviceData?.modelNumber },
    { label: localizationService.toLanguageString('custom.firmwareVersion', enMessages.custom.firmwareVersion), value: deviceData?.firmwareVersion },
    ...(deviceType === 'endpoint' ? [{ label: localizationService.toLanguageString('custom.hardwareVersion', enMessages.custom.hardwareVersion), value: deviceData?.hardwareVersion }] : [])
  ];

  return (
    <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
      <div className="flex flex-col gap-3">
        {fields.map((field, index) => (
          <InfoField key={index} label={field.label} value={field.value} />
        ))}
      </div>
    </div>
  );
};

const StatusConfigurationTile = ({ deviceData, deviceType }: DeviceDetailsHeaderProps) => {
  const localizationService = useLocalization();
  const InfoField = ({ label, value, showTooltip = false }: { label: string; value?: string; showTooltip?: boolean }) => (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-gray-500 font-medium">{label}:</span>
      {showTooltip ? (
        <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
          <span className="text-sm font-semibold text-gray-800 truncate overflow-hidden w-full block" title={value || '-'}>{value || '-'}</span>
        </Tooltip>
      ) : (
        <span className="text-sm font-semibold text-gray-800">{value || '-'}</span>
      )}
    </div>
  );

  const fields = [
    { label: localizationService.toLanguageString('custom.location', enMessages.custom.location), value: deviceData?.location, showTooltip: true },
    ...(deviceType === 'endpoint' ? [{ label: localizationService.toLanguageString('custom.asset', enMessages.custom.asset), value: deviceData?.asset, showTooltip: true }] : []),
    ...(deviceType === 'headUnit' ? [{ label: localizationService.toLanguageString('custom.consumable', enMessages.custom.consumable), value: deviceData?.consumable, showTooltip: true }] : []),
    { label: localizationService.toLanguageString('custom.inactivityTimeoutInMinutes', enMessages.custom.inactivityTimeoutInMinutes), value: `${deviceData?.inactivityTimeoutInMinutes} min`, showTooltip: false }
  ];

  return (
    <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
      <div className="flex flex-col gap-3">
        {fields.map((field, index) => (
          <InfoField key={index} label={field.label} value={field.value} showTooltip={field.showTooltip} />
        ))}
      </div>
    </div>
  );
};

const LastActivityTile = ({ deviceData, deviceType }: DeviceDetailsHeaderProps) => {
  const localizationService = useLocalization();
  const formatDate = (dateValue: any) => {
    const { displayText } = formatSmartDate(dateValue, localizationService, enMessages);
    return displayText;
  };

  const formatDateTooltip = (dateValue: any) => {
    const { tooltipText } = formatSmartDate(dateValue, localizationService, enMessages);
    return tooltipText;
  };

  const InfoField = ({ label, value, tooltip }: { label: string; value?: string; tooltip?: string }) => (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-gray-500 font-medium">{label}:</span>
      {tooltip ? (
        <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
          <span className="text-sm font-semibold text-gray-800" title={tooltip}>{value || '-'}</span>
        </Tooltip>
      ) : (
        <span className="text-sm font-semibold text-gray-800">{value || '-'}</span>
      )}
    </div>
  );

  const fields = [
    { 
      label: deviceType === 'endpoint' ? localizationService.toLanguageString('custom.lastSync', enMessages.custom.lastSync) : localizationService.toLanguageString('custom.lastActivity', enMessages.custom.lastActivity), 
      value: formatDate(deviceType === 'endpoint' ? deviceData?.lastSync : deviceData?.lastActivity),
      tooltip: formatDateTooltip(deviceType === 'endpoint' ? deviceData?.lastSync : deviceData?.lastActivity)
    },
    ...(deviceType === 'endpoint' ? [
      { 
        label: localizationService.toLanguageString('custom.lastHeartbeat', enMessages.custom.lastHeartbeat), 
        value: formatDate(deviceData?.lastHeartbeat),
        tooltip: formatDateTooltip(deviceData?.lastHeartbeat)
      },
      { 
        label: localizationService.toLanguageString('custom.lastGreaseEvent', enMessages.custom.lastGreaseEvent), 
        value: formatDate(deviceData?.lastGreaseEvent),
        tooltip: formatDateTooltip(deviceData?.lastGreaseEvent)
      }
    ] : [])
  ];

  return (
    <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
      <div className="flex flex-col gap-3">
        {fields.map((field, index) => (
          <InfoField key={index} label={field.label} value={field.value} tooltip={field.tooltip} />
        ))}
      </div>
    </div>
  );
};

export const DeviceDetailsInfoTiles: React.FC<DeviceDetailsHeaderProps> = ({ deviceData, deviceType }) => {

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
      <DeviceImageTile deviceData={deviceData} deviceType={deviceType} />
      <DeviceInfoTile deviceData={deviceData} deviceType={deviceType} />
      <StatusConfigurationTile deviceData={deviceData} deviceType={deviceType} />
      <LastActivityTile deviceData={deviceData} deviceType={deviceType} />
    </div>
  );
}; 